import type { RowData } from "@tanstack/react-table";
import type { DataGridCell } from "../columns/types";
import { getCellAddressFromTarget } from "../focus/navigation";
import type { DataGridCellChange, DataGridInstance } from "../types";
import { type EditableCell, getEditableCell } from "./cellAccess";
import {
    type CellRect,
    createCellLookup,
    getActiveRect,
    getColumnIndex,
    type GridPoint,
    selectRect,
} from "./cellRect";
import { getCellCopyText, parseCellChange } from "./cellText";
import { type EditContext, emitChanges, toCellChange } from "./editActions";
import { toEditorValue } from "./editorValues";
import { getFillRect, getFillSourcePoint, isInsideRect } from "./fill";

const pointFromElement = <TData extends RowData>(
    context: EditContext<TData>,
    element: Element | null,
): GridPoint | null => {
    const address = getCellAddressFromTarget(element);
    const { table } = context.instance;
    const row = address
        ? table.getPrePaginatedRowModel().rowsById[address.rowId]
        : undefined;
    if (!address || !row || !context.controller.root?.contains(element)) {
        return null;
    }
    const columnIndex = getColumnIndex(table, address.columnId);
    return columnIndex < 0
        ? null
        : { rowIndex: row.getDisplayIndex(), columnIndex };
};

/**
 * A filled cell's change. In the source's column the editor value copies as
 * is (a picker label can belong to more than one option); across columns the
 * copy text parses through the target's editor, as a paste does.
 */
const toFillChange = <TData extends RowData>({
    instance,
    editable,
    sourceCell,
}: {
    instance: DataGridInstance<TData>;
    editable: EditableCell<TData>;
    sourceCell: DataGridCell<TData>;
}): DataGridCellChange | null =>
    sourceCell.column.id === editable.cell.column.id
        ? toCellChange({
              ...editable,
              value: toEditorValue({
                  editor: editable.editor,
                  value: sourceCell.getValue(),
              }),
          })
        : parseCellChange({
              instance,
              editable,
              text: getCellCopyText(sourceCell),
          });

const buildFillChanges = <TData extends RowData>(
    context: EditContext<TData>,
    { source, target }: { source: CellRect; target: CellRect },
) => {
    const { instance } = context;
    const getCellAt = createCellLookup(instance.table);
    const changes: DataGridCellChange[] = [];
    for (let rowIndex = target.top; rowIndex <= target.bottom; rowIndex++) {
        for (
            let columnIndex = target.left;
            columnIndex <= target.right;
            columnIndex++
        ) {
            const point = { rowIndex, columnIndex };
            const cell = isInsideRect(source, point)
                ? undefined
                : getCellAt(point);
            const editable = cell ? getEditableCell(instance, cell) : null;
            const sourceCell = getCellAt(getFillSourcePoint({ source, point }));
            const change =
                editable && sourceCell
                    ? toFillChange({ instance, editable, sourceCell })
                    : null;
            if (change) {
                changes.push(change);
            }
        }
    }
    return changes;
};

const FILL_ATTRIBUTE = "data-fill-drag";

/**
 * Drag from the fill handle: the range outline follows the pointer as a
 * tinted preview, and releasing repeats the source cells into the new area
 * as one change. The source stays selected.
 * A cancelled gesture (`pointercancel`) or `signal` aborting (the grid
 * unmounting) restores the source and changes nothing.
 */
export const startFillDrag = <TData extends RowData>({
    context,
    signal,
}: {
    context: EditContext<TData>;
    signal: AbortSignal;
}) => {
    const { table } = context.instance;
    const source = getActiveRect(table);
    const selection = table.atoms.cellSelection.get();
    const { viewport } = context.controller;
    if (!source) {
        return;
    }
    let target = source;
    const drag = new AbortController();
    const stop = () => {
        drag.abort();
        viewport?.removeAttribute(FILL_ATTRIBUTE);
        table.setCellSelection(selection);
    };
    viewport?.setAttribute(FILL_ATTRIBUTE, "");
    const handleMove = (event: PointerEvent) => {
        const pointer = pointFromElement(
            context,
            document.elementFromPoint(event.clientX, event.clientY),
        );
        const next = pointer ? getFillRect({ source, pointer }) : target;
        if (
            next.top !== target.top ||
            next.bottom !== target.bottom ||
            next.left !== target.left ||
            next.right !== target.right
        ) {
            target = next;
            selectRect({ table, rect: target });
        }
    };
    const handleUp = () => {
        stop();
        emitChanges(context, buildFillChanges(context, { source, target }));
    };
    const listenerOptions = { signal: drag.signal };
    document.addEventListener("pointermove", handleMove, listenerOptions);
    document.addEventListener("pointerup", handleUp, listenerOptions);
    document.addEventListener("pointercancel", stop, listenerOptions);
    signal.addEventListener("abort", stop, listenerOptions);
};
